import React, { useState, useEffect, useRef } from "react";
import { submitRateReviewForm } from "../../services/rateReviewApiService";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import { Loader } from "../../../../shared";
import { getCompanySalesReps } from "../../../../services/userService";
import { AUTHORIZE_NET_COMPANY } from "../../../../utils/constants";
import { 
  validateRateReviewForm, 
  validateRateReviewField
} from "../../utils/rateReviewValidation";
import { RATE_REVIEW_CONSTANTS } from "../../constants/rateReviewConstants";
import { CameraOutlined } from "@ant-design/icons";
import uploadIcon from "../../../../assets/images/upload-icon.svg";
import logo from "../../../../assets/images/logo-colored.png";
import "./RateReviewForm.scss";

/**
 * Public Rate Review Form Component
 */
const RateReviewForm = () => {
  const message = useMessageApi();
  const [isLoading, setIsLoading] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [isLoadingSalesReps, setIsLoadingSalesReps] = useState(false);
  const [showSalesRepDropdown, setShowSalesRepDropdown] = useState(false);
  const salesRepDropdownRef = useRef(null);
  const isSelectingRef = useRef(false);
  const processingStatementsFileRef = useRef(null);
  const cameraFileRef = useRef(null);
  const [processingStatementsFiles, setProcessingStatementsFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    phoneNumber: "",
    Gateway: "",
    salesRep: "",
    salesRepEmail: "",
    country: "USA",
    notes: "",
    errors: {},
  });

  useEffect(() => {
    const fetchSalesReps = async () => {
      setIsLoadingSalesReps(true);
      try {
        const authorizeNetReps = await getCompanySalesReps(AUTHORIZE_NET_COMPANY).catch(() => []);
        
        authorizeNetReps.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        setSalesReps(authorizeNetReps);
      } catch (error) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to load sales representatives";
        message.error(errorMessage, 2.5);
      } finally {
        setIsLoadingSalesReps(false);
      }
    };

    fetchSalesReps();
  }, [message]);

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (salesRepDropdownRef.current && !salesRepDropdownRef.current.contains(event.target)) {
        // Use a small delay to allow click events on dropdown items to process first
        setTimeout(() => {
          setShowSalesRepDropdown(false);
        }, RATE_REVIEW_CONSTANTS.DROPDOWN_INTERACTION_DELAY_MS);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Filter sales representatives based on search term
   */
  const getFilteredSalesReps = () => {
    if (!formData.salesRep) return salesReps;
    const searchTerm = formData.salesRep.toLowerCase();
    return salesReps.filter(rep => 
      rep.name?.toLowerCase().includes(searchTerm)
    );
  };

  /**
   * Check if the current salesRep value is a valid selection from the list
   */
  const isValidSalesRepSelection = () => {
    if (!formData.salesRep || !formData.salesRep.trim()) return false;
    const trimmedValue = formData.salesRep.trim();
    return salesReps.some(rep => rep.name === trimmedValue);
  };

  /**
   * Handle sales representative selection
   */
  const handleSalesRepSelect = (repName, repEmail, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Set flag to prevent blur handler from interfering
    isSelectingRef.current = true;
    
    setFormData((prev) => ({
      ...prev,
      salesRep: repName,
      salesRepEmail: repEmail || "",
      errors: {
        ...prev.errors,
        salesRep: undefined,
      },
    }));
    setShowSalesRepDropdown(false);
    
    // Reset flag after a short delay to allow blur handler to complete
    setTimeout(() => {
      isSelectingRef.current = false;
    }, RATE_REVIEW_CONSTANTS.DROPDOWN_INTERACTION_DELAY_MS);
  };

  /**
   * Handle sales representative input change
   */
  const handleSalesRepChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      salesRep: value,
      errors: {
        ...prev.errors,
        salesRep: undefined,
      },
    }));
    // Show dropdown on any input so list filters dynamically as user types
    setShowSalesRepDropdown(true);
  };

  /**
   * Handle sales rep blur - no validation, just update email if found
   */
  const handleSalesRepBlur = (e) => {
    // Don't process if clicking inside the dropdown or if we're in the middle of selecting
    if (isSelectingRef.current) {
      return;
    }
    
    if (salesRepDropdownRef.current && salesRepDropdownRef.current.contains(e.relatedTarget)) {
      return;
    }
    
    // Use a delay to allow click events on dropdown items to process first
    setTimeout(() => {
      // Don't process if we just made a selection
      if (isSelectingRef.current) {
        return;
      }
      
      // No validation - just update email if found and clear any errors
      if (formData.salesRep && formData.salesRep.trim()) {
        const selectedRep = salesReps.find(rep => rep.name === formData.salesRep.trim());
        setFormData((prev) => ({
          ...prev,
          salesRepEmail: selectedRep?.email || "",
          errors: {
            ...prev.errors,
            salesRep: undefined,
          },
        }));
      } else {
        // Clear email and errors if field is empty
        setFormData((prev) => ({
          ...prev,
          salesRepEmail: "",
          errors: {
            ...prev.errors,
            salesRep: undefined,
          },
        }));
      }
      setShowSalesRepDropdown(false);
    }, RATE_REVIEW_CONSTANTS.BLUR_HANDLER_DELAY_MS);
  };

  /**
   * Handle input change with validation
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // If the field is phoneNumber, strip all non-numeric characters
    if (name === "phoneNumber") {
      newValue = value.replace(/\D/g, "");
    }

    // If the field is Gateway, strip all non-numeric characters and limit to 7 digits
    if (name === "Gateway") {
      // Remove all non-numeric characters
      newValue = value.replace(/\D/g, "");
      // Limit to maximum 7 digits
      if (newValue.length > 7) {
        newValue = newValue.substring(0, 7);
      }
      // Update value without validation
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
        errors: {
          ...prev.errors,
          Gateway: undefined, // Clear any existing errors
        },
      }));
      return; // Exit early for Gateway field
    }

    // Validate the field using Rate Review validation utility
    const errorMsg = validateRateReviewField(
      name, 
      newValue, 
      { ...formData, [name]: newValue },
      { salesRepsList: salesReps }
    );

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      errors: {
        ...prev.errors,
        [name]: errorMsg, // Will be undefined if valid, which removes the error
      },
    }));
  };

  /**
   * Validate form data using Rate Review validation utility
   */
  const validateForm = () => {
    const errors = validateRateReviewForm(formData, {
      salesRepsList: salesReps,
      processingStatementsFiles: processingStatementsFiles
    });

    setFormData((prev) => ({
      ...prev,
      errors,
    }));

    const isValid = Object.keys(errors).length === 0;
    return isValid;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      message.error("Please fill in all required fields correctly", 3);
      return;
    }

    setIsLoading(true);

    try {
      // Prepare data for API (map Gateway to gatewayId and notes to Notes1)
      const gatewayIdValue = formData.Gateway?.trim();
      const notesValue = formData.notes?.trim();
      
      const salesRepValue = formData.salesRep?.trim();
      const salesRepEmailValue = formData.salesRepEmail?.trim();
      
      const countryValue = formData.country?.trim();
      
      // Use FormData if files are present, otherwise use JSON
      const hasFiles = processingStatementsFiles && processingStatementsFiles.length > 0;
      
      let apiData;
      
      if (hasFiles) {
        // Use FormData for file uploads
        apiData = new FormData();
        apiData.append('firstName', formData.firstName);
        apiData.append('lastName', formData.lastName);
        apiData.append('businessName', formData.businessName);
        apiData.append('email', formData.email);
        apiData.append('phoneNumber', formData.phoneNumber);
        
        if (gatewayIdValue) apiData.append('gatewayId', gatewayIdValue);
        if (salesRepValue) apiData.append('salesRep', salesRepValue);
        if (salesRepEmailValue) apiData.append('salesRepEmail', salesRepEmailValue);
        if (countryValue) apiData.append('country', countryValue);
        if (notesValue) apiData.append('Notes1', notesValue);
        
        // Append all files
        processingStatementsFiles.forEach((file) => {
          apiData.append('processingStatements', file);
        });
      } else {
        // Use JSON for regular submission
        apiData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          businessName: formData.businessName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          ...(gatewayIdValue && { gatewayId: gatewayIdValue }),
          ...(salesRepValue && { salesRep: salesRepValue }),
          ...(salesRepEmailValue && { salesRepEmail: salesRepEmailValue }),
          ...(countryValue && { country: countryValue }),
          ...(notesValue && { Notes1: notesValue }),
        };
      }

      const response = await submitRateReviewForm(apiData);

      if (response.success) {
        message.success("Rate review request submitted successfully!", 3);
        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          businessName: "",
          email: "",
          phoneNumber: "",
          Gateway: "",
          salesRep: "",
          salesRepEmail: "",
          country: "USA",
          notes: "",
          errors: {},
        });
        setProcessingStatementsFiles([]);
        if (processingStatementsFileRef.current) {
          processingStatementsFileRef.current.value = '';
        }
        if (cameraFileRef.current) {
          cameraFileRef.current.value = '';
        }
      } else {
        throw new Error(response.error || "Failed to submit rate review");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to submit rate review request. Please try again.";
      message.error(errorMessage, 4);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Validate file type - only allow PDF and images
   * Uses constants from RATE_REVIEW_CONSTANTS for allowed file types
   */
  const isValidFileType = (file) => {
    return RATE_REVIEW_CONSTANTS.ALLOWED_FILE_TYPES.includes(file.type);
  };

  /**
   * Handle file selection for Processing Statements
   */
  const handleProcessingStatementsFileSelection = (selectedFiles) => {
    const filesArray = Array.from(selectedFiles);
    const currentFileCount = processingStatementsFiles.length;
    const newFileCount = filesArray.length;
    
    // Check if adding these files would exceed the limit
    const maxFiles = RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES;
    if (currentFileCount + newFileCount > maxFiles) {
      const remainingSlots = maxFiles - currentFileCount;
      if (remainingSlots <= 0) {
        message.error(`File limit reached! You already have ${currentFileCount} file(s). Please remove some files before adding more.`, 3);
      } else {
        message.error(`Too many files selected! You can only add ${remainingSlots} more file(s). Please select fewer files.`, 3);
      }
      return;
    }
    
    // Validate file types
    const invalidFiles = filesArray.filter(file => !isValidFileType(file));
    if (invalidFiles.length > 0) {
      const allowedExtensions = RATE_REVIEW_CONSTANTS.ALLOWED_FILE_EXTENSIONS.join(', ');
      message.error(`Only ${allowedExtensions} files are allowed for Processing Statements.`, 3);
      return;
    }
    
    // Check file size
    const maxFileSize = RATE_REVIEW_CONSTANTS.MAX_FILE_SIZE;
    const oversizedFiles = filesArray.filter(file => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      message.error(`File size too large. Maximum ${maxSizeMB}MB per file allowed.`, 3);
      return;
    }
    
    // Add new files to existing files
    const updatedFiles = [...processingStatementsFiles, ...filesArray];
    setProcessingStatementsFiles(updatedFiles);
    
    // Clear processing statements error when files are added
    setFormData((prev) => ({
      ...prev,
      errors: {
        ...prev.errors,
        processingStatements: undefined,
      },
    }));
  };

  /**
   * Handle file input change
   */
  const handleProcessingStatementsFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleProcessingStatementsFileSelection(selectedFiles);
    
    // Reset the file input value to allow selecting the same files again
    e.target.value = '';
  };

  /**
   * Remove a specific file from Processing Statements
   */
  const handleRemoveProcessingStatementFile = (index) => {
    const updatedFiles = processingStatementsFiles.filter((_, i) => i !== index);
    setProcessingStatementsFiles(updatedFiles);
    
    // Validate processing statements after removal
    // If no files remain, show error; otherwise clear error
    if (updatedFiles.length === 0) {
      setFormData((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          processingStatements: "At least one Processing Statement file is required",
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          processingStatements: undefined,
        },
      }));
    }
  };

  /**
   * Handle drag and drop for Processing Statements
   */
  const handleProcessingStatementsDrag = (e, isOver) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(isOver);
  };

  const handleProcessingStatementsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleProcessingStatementsFileSelection(droppedFiles);
  };

  const handleProcessingStatementsUploadAreaClick = () => {
    if (processingStatementsFileRef.current) {
      processingStatementsFileRef.current.click();
    }
  };

  /**
   * Handle camera file input change
   */
  const handleCameraFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleProcessingStatementsFileSelection(selectedFiles);
    
    // Reset the file input value to allow selecting the same files again
    e.target.value = '';
  };

  /**
   * Handle camera upload area click
   */
  const handleCameraUploadAreaClick = () => {
    if (cameraFileRef.current) {
      cameraFileRef.current.click();
    }
  };

  /**
   * Format file size for display
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Handle reset form
   */
  const handleReset = () => {
    setFormData({
      firstName: "",
      lastName: "",
      businessName: "",
      email: "",
      phoneNumber: "",
      Gateway: "",
      salesRep: "",
      salesRepEmail: "",
      country: "USA",
      notes: "",
      errors: {},
    });
    setProcessingStatementsFiles([]);
    setShowSalesRepDropdown(false);
    setIsDragOver(false);
    if (processingStatementsFileRef.current) {
      processingStatementsFileRef.current.value = '';
    }
    if (cameraFileRef.current) {
      cameraFileRef.current.value = '';
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />

      {/* Header of the Rate Review Form */}
      <header className="merchant-form-header rate-review-form-header">
        <div className="container">
          <div className="row align-items-center">
            {/* Left side: Logo section */}
            <div className="col-5 col-md-5">
              <a href="#" aria-label="Homepage">
                <img src={logo} alt="Company Logo" />
              </a>
            </div>

            {/* Right side: Rate Review Title - Visible on Mobile and Tablet */}
            <div className="col-7 col-md-7 d-lg-none">
              <div className="header-right-section text-end">
                <h1 className="form-heading form-heading-inline mb-0">
                  Rate Review
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section>
        <div className="container">
          <div className="new-merchant-submission">
            {/* Desktop: Center aligned heading */}
            <h1 className="text-center mb-5 form-heading d-none d-lg-block">
              Rate Review
            </h1>

            <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                {/* Contact Details Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <p>Contact Details</p>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${
                          formData.errors.firstName ? 'is-invalid' : formData.firstName ? 'is-valid' : ''
                        }`}
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="firstName">
                        First Name
                      </label>
                      {formData.errors.firstName && (
                        <div className="invalid-tooltip">{formData.errors.firstName}</div>
                      )}
                      {formData.firstName && !formData.errors.firstName && (
                        <div className="valid-tooltip">First Name is valid</div>
                      )}
                    </div>
                  </div>
                  <div className="col-6 col-md-3 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${
                          formData.errors.lastName ? 'is-invalid' : formData.lastName ? 'is-valid' : ''
                        }`}
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="lastName">
                        Last Name
                      </label>
                      {formData.errors.lastName && (
                        <div className="invalid-tooltip">{formData.errors.lastName}</div>
                      )}
                      {formData.lastName && !formData.errors.lastName && (
                        <div className="valid-tooltip">Last Name is valid</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-3 mb-3">
                    <div className="form-floating">
                      <input
                        type="email"
                        className={`form-control ${
                          formData.errors.email ? 'is-invalid' : formData.email ? 'is-valid' : ''
                        }`}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="email">
                        Email Address
                      </label>
                      {formData.errors.email && (
                        <div className="invalid-tooltip">{formData.errors.email}</div>
                      )}
                      {formData.email && !formData.errors.email && (
                        <div className="valid-tooltip">Email Address is valid</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-3 mb-3 phone-number-field">
                    <div className="form-floating">
                      <input
                        type="tel"
                        className={`form-control ${
                          formData.errors.phoneNumber ? 'is-invalid' : formData.phoneNumber ? 'is-valid' : ''
                        }`}
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="phoneNumber">
                        Phone Number
                      </label>
                      {formData.errors.phoneNumber && (
                        <div className="invalid-tooltip">{formData.errors.phoneNumber}</div>
                      )}
                      {formData.phoneNumber && !formData.errors.phoneNumber && (
                        <div className="valid-tooltip">Phone Number is valid</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Business Information Section - Business Name, Gateway ID, and Sales Representative in one row */}
                <div className="row mb-4">
                  <div className="col-12 col-md-4 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${
                          formData.errors.businessName ? 'is-invalid' : formData.businessName ? 'is-valid' : ''
                        }`}
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder=" "
                        required
                      />
                      <label htmlFor="businessName">
                        Business Name
                      </label>
                      {formData.errors.businessName && (
                        <div className="invalid-tooltip">{formData.errors.businessName}</div>
                      )}
                      {formData.businessName && !formData.errors.businessName && (
                        <div className="valid-tooltip">Business Name is valid</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-4 mb-3">
                    <div className="form-floating">
                      <input
                        type="text"
                        className={`form-control ${
                          formData.errors.Gateway ? 'is-invalid' : (formData.Gateway && formData.Gateway.length === 7 && !formData.errors.Gateway) ? 'is-valid' : ''
                        }`}
                        id="Gateway"
                        name="Gateway"
                        value={formData.Gateway}
                        onChange={handleChange}
                        onBlur={() => {
                          // No validation - just clear any existing errors
                          setFormData((prev) => ({
                            ...prev,
                            errors: {
                              ...prev.errors,
                              Gateway: undefined,
                            },
                          }));
                        }}
                        placeholder=" "
                        autoComplete="off"
                        maxLength={7}
                        inputMode="numeric"
                      />
                      <label htmlFor="Gateway">
                        Gateway ID
                      </label>
                      {formData.errors.Gateway && (
                        <div className="invalid-tooltip">{formData.errors.Gateway}</div>
                      )}
                      {formData.Gateway && !formData.errors.Gateway && formData.Gateway.length === 7 && (
                        <div className="valid-tooltip">Gateway ID is valid</div>
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-md-4 mb-3">
                    <div className="form-floating position-relative" ref={salesRepDropdownRef}>
                      <input
                        type="text"
                        className={`form-control ${
                          formData.errors.salesRep ? 'is-invalid' : (formData.salesRep && isValidSalesRepSelection()) ? 'is-valid' : ''
                        }`}
                        id="salesRep"
                        name="salesRep"
                        value={formData.salesRep}
                        onChange={handleSalesRepChange}
                        onBlur={handleSalesRepBlur}
                        onFocus={() => {
                          // Show list when field is clicked/focused
                          setShowSalesRepDropdown(true);
                        }}
                        placeholder=" "
                        autoComplete="off"
                      />
                      <label htmlFor="salesRep">
                        Sales Representative
                      </label>
                      {formData.errors.salesRep && (
                        <div className="invalid-tooltip">{formData.errors.salesRep}</div>
                      )}
                      {formData.salesRep && !formData.errors.salesRep && (
                        <div className="valid-tooltip">Sales Representative selected</div>
                      )}

                      {/* Dropdown List - show when opened; filtered by getFilteredSalesReps as user types */}
                      {showSalesRepDropdown && (
                        <div className="sales-rep-dropdown">
                          {isLoadingSalesReps ? (
                            <div className="dropdown-item text-muted">Loading sales representatives...</div>
                          ) : getFilteredSalesReps().length > 0 ? (
                            getFilteredSalesReps().map((rep) => (
                              <div
                                key={rep.id || rep.name}
                                className="dropdown-item"
                                onMouseDown={(e) => {
                                  // Set flag immediately to prevent blur from interfering
                                  isSelectingRef.current = true;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSalesRepSelect(rep.name, rep.email, e);
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSalesRepSelect(rep.name, rep.email, e);
                                  }
                                }}
                              >
                                {rep.name}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-item text-muted">No sales representatives found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Country Selection Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <p>Country</p>
                  </div>
                  <div className="col-12 col-md-3 d-flex gap-3 gap-md-5 align-items-center">
                    {/* Radio option: USA */}
                    <div className="radio me-5">
                      <input
                        id="USA"
                        type="radio"
                        name="country"
                        value="USA"
                        checked={formData.country === "USA"}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                      <label htmlFor="USA">USA</label>
                    </div>

                    {/* Radio option: Canada */}
                    <div className="radio">
                      <input
                        id="Canada"
                        type="radio"
                        name="country"
                        value="Canada"
                        checked={formData.country === "Canada"}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                      <label htmlFor="Canada">Canada</label>
                    </div>
                  </div>
                </div>

                {/* Processing Statements Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <p>Processing Statements</p>
                  </div>
                  
                  {/* Show upload area when files are less than maximum allowed */}
                  {processingStatementsFiles.length < RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES && (
                    <>
                      <div className="col-12 col-lg-3 mb-3 drag-drop-field">
                        <div 
                          className={`upload-file position-relative ${isDragOver ? 'drag-over' : ''} ${formData.errors.processingStatements ? 'is-invalid' : ''}`}
                          onDragOver={(e) => handleProcessingStatementsDrag(e, true)}
                          onDragEnter={(e) => handleProcessingStatementsDrag(e, true)}
                          onDragLeave={(e) => handleProcessingStatementsDrag(e, false)}
                          onDrop={handleProcessingStatementsDrop}
                          onClick={handleProcessingStatementsUploadAreaClick}
                          style={{ cursor: 'pointer' }}
                        >
                          <label className="file" onClick={(e) => e.stopPropagation()}>
                            <div>
                              <img className="mb-2" src={uploadIcon} alt="upload-icon" />
                            </div>
                            <div>
                              Drag & Drop Files or <span className="browse-text">Browse</span>
                            </div>
                            <small className="text-muted">
                              {processingStatementsFiles.length === 0 
                                ? `Max ${RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES} files (${RATE_REVIEW_CONSTANTS.ALLOWED_FILE_EXTENSIONS.join(', ').replace(/\./g, '')} only)` 
                                : `${RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES - processingStatementsFiles.length} more file(s) allowed`
                              }
                            </small>
                            {/* Hidden file input */}
                            <input
                              type="file"
                              ref={processingStatementsFileRef}
                              onChange={handleProcessingStatementsFileInputChange}
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
                              autoComplete="off"
                              style={{ display: 'none' }}
                            />
                          </label>
                          {formData.errors.processingStatements && (
                            <div className="invalid-tooltip">{formData.errors.processingStatements}</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Or Separator - Only visible on Mobile and Tablet */}
                      <div className="col-12 d-flex align-items-center justify-content-center mb-3 or-separator-wrapper d-lg-none">
                        <span className="or-separator">Or</span>
                      </div>
                      
                      {/* Camera Upload Option - Only visible on Mobile and Tablet */}
                      <div className={`col-12 mb-3 d-lg-none camera-upload-field ${processingStatementsFiles.length > 0 ? 'has-files' : ''}`}>
                        <div 
                          className={`upload-file position-relative ${formData.errors.processingStatements ? 'is-invalid' : ''}`}
                          onClick={handleCameraUploadAreaClick}
                          style={{ cursor: 'pointer' }}
                        >
                          <label className="file" onClick={(e) => e.stopPropagation()}>
                            <div>
                              <CameraOutlined className="camera-icon" />
                            </div>
                            <div>
                              <span className="browse-text">Use Camera To Take Image</span>
                            </div>
                            <small className="text-muted">
                              {processingStatementsFiles.length === 0 
                                ? `Max ${RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES} files` 
                                : `${RATE_REVIEW_CONSTANTS.MAX_PROCESSING_STATEMENTS_FILES - processingStatementsFiles.length} more file(s) allowed`
                              }
                            </small>
                            {/* Hidden file input with camera capture */}
                            <input
                              type="file"
                              ref={cameraFileRef}
                              onChange={handleCameraFileInputChange}
                              multiple
                              accept="image/*"
                              capture="environment"
                              autoComplete="off"
                              style={{ display: 'none' }}
                            />
                          </label>
                          {formData.errors.processingStatements && (
                            <div className="invalid-tooltip">{formData.errors.processingStatements}</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Show uploaded files */}
                  {processingStatementsFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="col-12 col-lg-3 mb-3">
                      <div className="upload-file position-relative">
                        <label className="file">
                          <div className="file-name-container">
                            {file.name}
                          </div>
                          <span className="sml">
                            {file.type}, {formatFileSize(file.size)}
                          </span>
                          <div className="mt-2">
                            <small className="text-muted">File {index + 1}</small>
                          </div>
                        </label>
                        {/* Remove file button */}
                        <button
                          type="button"
                          className="btn-close-file"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => handleRemoveProcessingStatementFile(index)}
                          tabIndex={0}
                        >
                          <i className="bi bi-x-circle-fill"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Notes Section */}
                <div className="form-floating mb-4 position-relative">
                  <input
                    type="text"
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder=" "
                    autoComplete="off"
                    className={`form-control ${
                      formData.errors.notes ? 'is-invalid' : formData.notes ? 'is-valid' : ''
                    }`}
                  />
                  <label htmlFor="notes">Notes</label>

                  {/* Show error tooltip when there is a validation error */}
                  {formData.errors.notes && (
                    <div className="invalid-tooltip">{formData.errors.notes}</div>
                  )}

                  {/* Show valid tooltip when notes have value and no errors */}
                  {formData.notes && !formData.errors.notes && (
                    <div className="valid-tooltip">Notes added</div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="row mt-3">
                  <div className="col-12 pt-3 text-end d-flex flex-row gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                    >
                      Reset Form
                    </button>
                    <button
                      type="submit"
                      className="btn merchant-submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading..." : "Submit"}
                    </button>
                  </div>
                </div>
              </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default RateReviewForm;

